import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal
} from '@angular/core';
import { finalize, tap } from 'rxjs';
import { ProjectStateService } from '../state/project-state.service';
import { AcsMode } from '../state/project.interface';
import { AuthStateService } from '../state/auth-state.service';
import { Router } from '@angular/router';
import { Routing } from '../../../app.routes';
import { FormGroup } from '@angular/forms';
import { SaveProjectRequest } from '../http/dto';
import { DatePipe } from '@angular/common';
import { StepperService } from '../stepper.service';
import { IAcsHttpService } from '../http/acs/acs-http-service.interface';
import { ACS_HTTP_SERVICE } from '../http/http-service.token';
import { Beneficiary } from '../../../core/api/models/beneficiary';
import { PricingInformations } from '../../../core/api/models/pricing-informations';
import { ExpatClient } from '../../../core/api/models/expat-client';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionFeatureService {
  readonly acsHttpService: IAcsHttpService = inject(ACS_HTTP_SERVICE);
  readonly authStateService: AuthStateService = inject(AuthStateService);
  readonly router: Router = inject(Router);
  readonly projectStateService: ProjectStateService =
    inject(ProjectStateService);
  readonly stepperService: StepperService = inject(StepperService);
  readonly datePipe: DatePipe = new DatePipe('fr-FR');
  private _isLoading: WritableSignal<boolean> = signal(false);

  constructor() {}

  get isLoading() {
    return computed(() => this._isLoading());
  }

  public getClientInfo(uuidClient: string, producerCode: string, mode: string) {
    return this.acsHttpService.getExpatClientInfos(uuidClient).pipe(
      tap((value) => {
        this.projectStateService.updateProjectInfo({
          client: value,
          producer_code: producerCode,
          mode: mode as AcsMode,
          subscription: false
        });
        this.projectStateService.updateClient(value);
      })
    );
  }

  public getProjectInfo(
    uuidProject: string,
    producerCode: string|null,
    mode: string|null
  ) {
    return this.acsHttpService.getProjectByUuid(uuidProject).pipe(
      tap((value) => {
        this.projectStateService.updateProjectInfo({
          beneficiaries: value.beneficiaries,
          product: value.product,
          pricing: value.pricing,
          producer_code: producerCode,
          mode: mode as AcsMode
        });
        this.projectStateService.updateClient(value.client)
      })
    );
  }

  public newProjectForSameClient() {
    this._isLoading.set(true);
    this.acsHttpService
      .getInfoForNewProjectForSameClient(this.authStateService.selectAuthState()().token_producer!)
      .pipe(finalize(() => this._isLoading.set(false)))
      .subscribe({
        error: (_) => {
          console.error('Error when creating new project');
        },
        next: (value) => {
          this.authStateService.updateData(value);
          this.projectStateService.setIsNewClient(false);
          this.stepperService.step.set(0);
          this.stepperService.isVisible.set(true);
          this.router.navigate([Routing.PROJECT]).then();
        }
      });
  }

  public newProjectForNewClient() {
    this.projectStateService.reset();
    this.authStateService.reset();
    this.stepperService.step.set(0);
    this.stepperService.isVisible.set(true);
    this.router.navigate([Routing.PROJECT]).then();
  }

  public validateStep1(form: FormGroup) {
    const formValue = form.value;
    if (new Date(formValue.effect_date).getTimezoneOffset() > 0) {
      formValue.effect_date = `${formValue.effect_date.getFullYear()}-${(formValue.effect_date.getMonth() + 1).toString().padStart(2, '0')}-${formValue.effect_date.getUTCDate().toString().padStart(2, '0')}T00:00:00Z`;
    } else {
      formValue.effect_date =
        this.datePipe.transform(formValue.effect_date, 'yyyy-MM-dd') +
        'T00:00:00Z';
    }
    this.projectStateService.updatePricingInformations(formValue);
  }

  public validateStep2() {
    return this.acsHttpService.postStep2(this.toSaveProjectRequest());
  }

  public validateSubmitStep3(form: FormGroup) {
    const dataToSave = this.updateDataForStep3(form);
    return this.acsHttpService.postStep3Subscribe(dataToSave);
  }

  public validateSaveStep3(form: FormGroup) {
    const dataToSave = this.updateDataForStep3(form);
    return this.acsHttpService.postStep3Save(dataToSave);
  }

  private updateDataForStep3(form: FormGroup) {
    const dataToSave = this.toSaveProjectRequest();
    dataToSave.beneficiaries.forEach(
      (item) =>
        (item.acs_expat_beneficiary_maternity_option =
          item.category === form.get('beneficiaries')!.value ? 1 : 0)
    );
    this.projectStateService.updateExpatClientInfos(
      {
        ...dataToSave.client,
        beneficiaries: [...dataToSave.beneficiaries]
      },
      true
    );
    this.projectStateService.setProjectBeneficiaries(dataToSave.beneficiaries);
    return dataToSave;
  }

  public validateStep4() {
    const data = this.toSaveProjectRequest();
    data.subscription = true;
    return this.acsHttpService.postStep4(data);
  }

  private toSaveProjectRequest(): SaveProjectRequest {
    const pricing =
      this.projectStateService.getPricingInformations() ?? undefined;
    const client =
      this.projectStateService.selectExpatClientInfos() ?? undefined;
    const authData = this.authStateService.selectAuthState();
    const beneficiaries = this.ensureSouscripteur(
      [...this.projectStateService.selectProjectBeneficiaries()],
      client,
      pricing
    );
    this.projectStateService.setProjectBeneficiaries(beneficiaries);
    return {
      pricing: pricing,
      client: client,
      producer_code: authData().producerCode,
      beneficiaries: beneficiaries,
      token: authData().token ?? null,
      mode: authData().mode as AcsMode,
      product: pricing ? pricing.currency ?? null : null,
      subscription: false,
      token_producer: authData().token_producer ?? null
    };
  }

  private ensureSouscripteur(
    beneficiaries: Beneficiary[],
    client?: ExpatClient,
    pricing?: PricingInformations
  ): Beneficiary[] {
    if (!client) {
      return beneficiaries;
    }

    const existingIndex = beneficiaries.findIndex(
      (b) => b.category === 'Souscripteur'
    );
    const subscriber: Beneficiary = {
      category: 'Souscripteur',
      birth_date: client.birth_date,
      acs_expat_beneficiary_business_sector:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_business_sector ?? 0,
      acs_expat_beneficiary_email: client.email,
      // expat_project_maternity_option est le niveau d'option retenu sur le projet, pas le bénéficiaire
      // concerné : le reprendre ici marquait le souscripteur en plus du bénéficiaire réellement choisi.
      acs_expat_beneficiary_maternity_option:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_maternity_option ??
        0,
      acs_expat_beneficiary_nationality_country:
        pricing?.expat_project_nationality ??
        beneficiaries[existingIndex]?.acs_expat_beneficiary_nationality_country ??
        client.country ?? '',
      acs_expat_beneficiary_occupation:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_occupation ?? '',
      acs_expat_beneficiary_ppe:
        beneficiaries[existingIndex]?.acs_expat_beneficiary_ppe ?? false,
      first_name: client.first_name,
      last_name: client.last_name
    };

    if (existingIndex !== -1) {
      beneficiaries[existingIndex] = subscriber;
      return beneficiaries;
    }

    return [...beneficiaries, subscriber];
  }

  public setIsLoading(value: boolean) {
    this._isLoading.set(value);
  }
}
